import { Injectable, signal, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';

export type SupportedLocale = 'de' | 'en' | 'fr' | 'it';

const SUPPORTED_LOCALES: SupportedLocale[] = ['de', 'en', 'fr', 'it'];
const DEFAULT_LOCALE: SupportedLocale = 'de';
const STORAGE_KEY = 'cuminanza_lang';

@Injectable({ providedIn: 'root' })
export class LocaleService {
  private translate = inject(TranslateService);

  private readonly _locale = signal<SupportedLocale>(DEFAULT_LOCALE);
  readonly locale = this._locale.asReadonly?.() ?? this._locale;
  readonly supportedLocales = SUPPORTED_LOCALES;

  /**
   * Returns an Observable that completes when the initial language has been
   * loaded. Hand it to `provideAppInitializer` so the app waits for translations
   * before its first render — otherwise the pipe falls back to the key in
   * zoneless change-detection mode when the load resolves after first render.
   */
  init(): Observable<unknown> {
    this.translate.addLangs([...SUPPORTED_LOCALES]);
    return this.setLocale(this.resolveInitialLocale());
  }

  setLocale(locale: SupportedLocale): Observable<unknown> {
    if (!SUPPORTED_LOCALES.includes(locale)) return of(null);
    this._locale.set(locale);
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // localStorage may be unavailable (private mode); ignore.
    }
    // Workaround for ngx-translate v17: a `loadAndCompileTranslations` request
    // triggered during TranslateService construction (e.g. via `lang`/`fallbackLang`
    // in `provideTranslateService` config) caches a `_translationRequests` entry
    // whose internal `setTranslations` subscription silently no-ops — so the
    // store never gets populated even though the HTTP response arrives. Clearing
    // the cached request before calling `use()` forces a fresh `loadAndCompile`
    // and lets the store fill correctly.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cache = (this.translate as any)._translationRequests as
      Record<string, unknown> | undefined;
    if (cache) delete cache[locale];
    return this.translate.use(locale);
  }

  private resolveInitialLocale(): SupportedLocale {
    return this.readStoredLocale() ?? this.detectBrowserLocale() ?? DEFAULT_LOCALE;
  }

  private readStoredLocale(): SupportedLocale | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return SUPPORTED_LOCALES.includes(raw as SupportedLocale) ? (raw as SupportedLocale) : null;
    } catch {
      return null;
    }
  }

  private detectBrowserLocale(): SupportedLocale | null {
    const nav = typeof navigator !== 'undefined' ? navigator.language : '';
    const base = nav.toLowerCase().split('-')[0];
    return SUPPORTED_LOCALES.includes(base as SupportedLocale) ? (base as SupportedLocale) : null;
  }
}
